import {
    Button,
    Checkbox,
    Layout,
    MultipleTextField,
    TextField,
    Typography,
    useAlert
} from "matsuri-ui"
import {
    MatsuriFormsMultilineTextQuestionProps,
    MatsuriFormsQuestion,
    MatsuriFormsQuestionProvider,
    MatsuriFormsSelectQuestionProps,
    MatsuriFormsTextQuestionProps,
    useMatsuriForms
} from "matsuri-forms-sdk"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useNavigate } from "react-router-dom"
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react"
import styled from "@emotion/styled"
import CloseIcon from "@mui/icons-material/Close"
import { useAuthUser } from "../hooks/useAuthUser"
import { useFindUserById } from "../hooks/useUser"
import { useOperation } from "../hooks/useOperaion"
import TakePhotoIcon from "@/components/Icon/TakePhoto.svg"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import { TroubleReportHeader } from "@/parts/TroubleReport/TroubleReportHeader"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"
import { imageCompressToWebp, validateImageFile } from "@/utils/image"

export interface Picture {
    file: File
    description: string
    objectUrl?: string // メモリリーク防止のためのクリーンアップ用
}

const CoverButton = styled.button`
    min-height: 280px;

    width: 100%;

    & > div {
        background-color: rgba(65, 65, 65, 0.8);
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        & > p {
            color: white;
        }

        & > img {
            width: 20%;
            height: auto;
            margin: 1rem;
        }
    }
`

const BodyTitle = styled.h1`
    padding-bottom: 24px;
    color: ${theme.palette.intentions.text.primary};
    font-size: 20px;
    text-align: center;
`

const BodyArea = styled.div`
    padding: 12px;
`

const PictureArea = styled.div`
    padding: 12px 0;
    display: flex;
`

interface DescriptionContextState {
    description: string
    setDescription: (description: string) => void
}

const DescriptionContext = React.createContext<
    DescriptionContextState | undefined
>(undefined)

interface TroubleFormsContextState {
    id: string
    registeredQuestions: {
        listingId: string
        commonAreaId: string
        userId: string
        userName: string
        troubleId: string
        descriptionId: string
    }
    listingId?: string
    commonAreaId?: string
}

const TroubleFormsContext = React.createContext<
    TroubleFormsContextState | undefined
>(undefined)

const useTroubleForms = () => {
    const troubleForms = React.useContext(TroubleFormsContext)
    if (!troubleForms) {
        throw new Error(
            "useTroubleForms must be used within a TroubleFormsContext"
        )
    }
    return troubleForms
}

const troubleFormsDev = {
    id: "01H59K9EZJZ36CH3B764F4BBCC",
    registeredQuestions: {
        listingId: "1895334bb81cb",
        commonAreaId: "1895335f62d1f4",
        userId: "189533619dd165",
        userName: "18953363bca1b8",
        troubleId: "1895336741ff4",
        descriptionId: "01HARRTD9MJ11PQT1ETFPHJPC9"
    }
} satisfies Partial<TroubleFormsContextState>

const troubleFormsProd = {
    id: "01H51W0NHG20952C1FJHE7SJ74",
    registeredQuestions: {
        listingId: "18943c0561c3cd",
        commonAreaId: "189441cd23589",
        userId: "18943c0de28fb",
        userName: "189537a502913f",
        troubleId: "1894412eaf515",
        // ref: https://www.notion.so/matsuri-tech/6444e4baffef4d608ad11148537bcb7b?v=a6b3f5e1ee1345ef8bf6495f8b55538c&p=62e2cb35ac454a298ef3ce5e225803c4&pm=s
        descriptionId: "01J3WWYF71WNHEHJP7A8TG1YTK"
    }
} satisfies Partial<TroubleFormsContextState>

export interface TroubleItemProps {
    operationId: string
    troubleReportedPath: string
    skippable: boolean
    onSubmit: (input: {
        description: string
        blobs: Blob[]
    }) => Promise<{ data?: { id: string }; error?: Error }>
    onClose?: () => void
}

export const TroubleReport = ({
    operationId,
    troubleReportedPath,
    skippable,
    onSubmit,
    onClose
}: TroubleItemProps) => {
    const { t } = useTranslation()
    const { token } = useAuth()
    // useOperationは通常清掃、Operationいずれにも対応している
    const { data: operation } = useOperation(token, operationId)

    const { throwAlert } = useAlert()
    const [imgData, setImgData] = useState<Picture[]>([])

    // imgData の最新値を ref で保持する
    const imgDataRef = useRef<Picture[]>([])
    useEffect(() => {
        imgDataRef.current = imgData
    }, [imgData])

    // メモリリーク防止：コンポーネントアンマウント時に objectURL をクリーンアップ
    useEffect(() => {
        return () => {
            imgDataRef.current.forEach(img => {
                if (img.objectUrl) {
                    URL.revokeObjectURL(img.objectUrl)
                }
            })
        }
    }, [])

    const reset = useCallback(() => {
        setImgData(prev => {
            // 既存のobjectURLをクリーンアップ
            prev.forEach(img => {
                if (img.objectUrl) {
                    URL.revokeObjectURL(img.objectUrl)
                }
            })
            return []
        })
        // inputのvalueをクリアして同じファイルを再選択可能にする
        if (inputRef.current) {
            inputRef.current.value = ""
        }
    }, [])

    const navigate = useNavigate()
    const skip = useCallback(() => {
        navigate(troubleReportedPath)
    }, [navigate, troubleReportedPath])

    const submit = useCallback(
        async (content: string, pictures: Picture[]) => {
            try {
                const blobs = await Promise.all(
                    pictures.map(picture =>
                        imageCompressToWebp(picture.file, {
                            quality: 0.85,
                            maxWidth: 1500,
                            maxHeight: 1500
                        })
                    )
                )

                const { data, error } = await onSubmit({
                    description: content,
                    blobs
                })

                if (error) {
                    console.error("[TroubleReport] API呼び出しエラー", {
                        error,
                        errorMessage: error.message,
                        errorStack: error.stack
                    })

                    // エラー詳細を保持したまま投げる
                    throwAlert(error, {
                        errorMessage: {
                            happend: t("トラブル報告の送信に失敗しました"),
                            action: t(
                                "ネットワーク接続を確認してから、もう一度お試しください。問題が続く場合は、管理者にお問い合わせください。"
                            )
                        },
                        ignoreSuccess: true
                    })

                    return undefined
                }

                return data?.id
            } catch (error) {
                console.error("[TroubleReport] 予期しないエラー", {
                    error,
                    errorMessage:
                        error instanceof Error ? error.message : String(error),
                    errorStack: error instanceof Error ? error.stack : undefined
                })

                const userMessage = {
                    happend: t("アップロード中にエラーが発生しました"),
                    action: t(
                        "もう一度お試しください。問題が続く場合は、管理者にお問い合わせください。"
                    )
                }

                throwAlert(
                    error instanceof Error ? error : new Error(String(error)),
                    {
                        errorMessage: userMessage,
                        ignoreSuccess: true
                    }
                )

                return undefined
            }
        },
        [throwAlert, onSubmit, t]
    )

    const inputRef = useRef<HTMLInputElement>(null)

    const handleCoverClick = useCallback((event: React.SyntheticEvent) => {
        event.preventDefault()
        inputRef?.current?.click()
    }, [])

    const [description, setDescription] = useState("")

    const descriptionState = useMemo(
        () => ({ description, setDescription }),
        [description, setDescription]
    )

    const formRef = useRef<HTMLFormElement | null>(null)

    const [isUploading, setIsUploading] = useState(false)

    const formValue: TroubleFormsContextState = useMemo(
        () =>
            process.env.RUNTIME_ENV === "production"
                ? {
                      ...troubleFormsProd,
                      listingId: operation?.listingId,
                      commonAreaId: operation?.commonAreaId
                  }
                : {
                      ...troubleFormsDev,
                      listingId: operation?.listingId,
                      commonAreaId: operation?.commonAreaId
                  },
        [operation?.commonAreaId, operation?.listingId]
    )
    const { questions, errors, validate, getSubmissionValues, sendSubmission } =
        useMatsuriForms(formValue.id, {
            dev: process.env.NODE_ENV !== "production"
        })

    const handleSubmit = useCallback(
        async (event: React.SyntheticEvent) => {
            event.preventDefault()
            event.stopPropagation()

            if (!formRef.current) return

            setIsUploading(true)

            try {
                const { errors: validateError } = validate(formRef.current)
                if (validateError) {
                    setIsUploading(false)
                    return
                }

                const troubleId = await submit(description, imgData ?? "")

                if (!troubleId) {
                    setIsUploading(false)
                    return
                }

                const formValues = getSubmissionValues(formRef.current)

                formValues.push({
                    questionId: formValue.registeredQuestions.troubleId,
                    value: troubleId
                })

                const { error: matsuriFormError } =
                    await sendSubmission(formValues)

                throwAlert(matsuriFormError, {
                    errorMessage: {
                        happend: t("送信中にエラーが発生しました"),
                        action: t(
                            "しばらく時間を空けて試すか、管理者にお問い合わせください。"
                        )
                    },
                    ignoreSuccess: true,
                    onSuccess: () => {
                        navigate(troubleReportedPath)
                    }
                })

                setIsUploading(false)
            } catch (error) {
                throwAlert(
                    error instanceof Error
                        ? new Error(error.stack)
                        : new Error(String(error)),
                    {
                        errorMessage: {
                            happend: t("予期しないエラーが発生しました")
                        }
                    }
                )
            }
        },
        [
            description,
            formValue.registeredQuestions.troubleId,
            getSubmissionValues,
            imgData,
            navigate,
            sendSubmission,
            submit,
            throwAlert,
            troubleReportedPath,
            validate,
            t
        ]
    )

    return (
        <main>
            <TroubleReportHeader
                RightButton={
                    onClose
                        ? () => (
                              <Button icon={<CloseIcon />} onClick={onClose} />
                          )
                        : undefined
                }
            />

            <div>
                <BodyArea>
                    <BodyTitle>{t("こちらでよろしいですか？")}</BodyTitle>
                    <Typography>
                        {t("他に報告事項があればお書きください")}
                    </Typography>

                    <PictureArea>
                        {imgData.length > 0 ? null : (
                            <CoverButton
                                onClick={handleCoverClick}
                                aria-label={t("写真を撮影")}
                            >
                                <div>
                                    <img src={TakePhotoIcon} />
                                    <Typography>{t("写真を撮影")}</Typography>
                                </div>
                            </CoverButton>
                        )}
                        <input
                            multiple
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            onChange={async event => {
                                const fileList = event.currentTarget.files
                                if (!fileList) {
                                    return
                                }
                                const files = Array.from(fileList)

                                // 画像ファイルの読み込み可否を検証
                                const validFiles: File[] = []
                                for (const file of files) {
                                    const isValid =
                                        await validateImageFile(file)
                                    if (isValid) {
                                        validFiles.push(file)
                                    }
                                }

                                if (validFiles.length === 0) {
                                    throwAlert(
                                        new Error(
                                            "画像の読み込みに失敗しました"
                                        ),
                                        {
                                            errorMessage: {
                                                happend: t(
                                                    "画像を読み込めませんでした。もう一度お試しください"
                                                )
                                            }
                                        }
                                    )
                                    return
                                }

                                if (validFiles.length < files.length) {
                                    throwAlert(
                                        new Error(
                                            `${files.length - validFiles.length}枚の画像の読み込みに失敗しました`
                                        ),
                                        {
                                            errorMessage: {
                                                happend: t(
                                                    "一部の画像を読み込めませんでした。読み込めた画像のみ追加しました"
                                                )
                                            }
                                        }
                                    )
                                }

                                const images: Picture[] = validFiles.map(
                                    file => {
                                        const objectUrl =
                                            URL.createObjectURL(file)
                                        return {
                                            file,
                                            description: "",
                                            objectUrl
                                        }
                                    }
                                )

                                setImgData(prev => {
                                    const merged = [...prev, ...images]
                                    // クリーンアップ用の参照を最新の状態に同期する
                                    imgDataRef.current = merged
                                    return merged
                                })
                            }}
                            style={{
                                display: "none",
                                width: "auto",
                                height: "auto",
                                maxWidth: "100%",
                                maxHeight: "256px"
                            }}
                        />

                        {imgData.length === 0 ? null : (
                            <Layout.Column
                                justify={"center"}
                                alignItems="center"
                                gap={2}
                            >
                                {imgData.map((img, index) => (
                                    <div key={index}>
                                        <img
                                            src={img.objectUrl}
                                            width="auto"
                                            height="auto"
                                            css={css`
                                                max-width: 100%;
                                                max-height: 256px;
                                            `}
                                        />
                                    </div>
                                ))}
                            </Layout.Column>
                        )}
                    </PictureArea>
                </BodyArea>
                <div
                    css={css`
                        margin: 0 12px;
                    `}
                >
                    <TroubleFormsContext.Provider value={formValue}>
                        <form
                            css={css`
                                display: grid;
                                gap: 16px;
                            `}
                            ref={formRef}
                        >
                            <Typography variant="h3">
                                {t("トラブルの詳細を報告する")}
                            </Typography>

                            <DescriptionContext.Provider
                                value={descriptionState}
                            >
                                <MatsuriFormsQuestionProvider
                                    renderSelect={SelectQuestion}
                                    renderMultilineText={MultipleTextQuestion}
                                    renderText={RenderText}
                                >
                                    {questions.map(question => {
                                        return (
                                            <MatsuriFormsQuestion
                                                error={errors[question.id]}
                                                {...question}
                                                key={question.id}
                                            />
                                        )
                                    })}
                                </MatsuriFormsQuestionProvider>
                            </DescriptionContext.Provider>
                        </form>
                    </TroubleFormsContext.Provider>
                </div>
            </div>
            {skippable && (
                <Layout.Row justify={"center"} style={{ padding: "1em" }}>
                    <div>
                        <Button type="button" onClick={skip}>
                            {t("スキップ")}
                        </Button>
                    </div>
                </Layout.Row>
            )}
            <ReportFooter>
                <Layout.Column justify={"center"} gap={2}>
                    <div>
                        <Button
                            type="button"
                            variant="filled"
                            color="primary"
                            fullWidth
                            disabled={
                                (imgData.length > 0 ? false : true) ||
                                isUploading ||
                                !description
                            }
                            onClick={handleSubmit}
                        >
                            {isUploading
                                ? t("アップロード中です…")
                                : t("報告を送信する")}
                        </Button>
                    </div>
                    <div>
                        <Button type="button" fullWidth onClick={reset}>
                            {t("撮り直し")}
                        </Button>
                    </div>
                </Layout.Column>
            </ReportFooter>
        </main>
    )
}

const SelectQuestion = ({
    multiple,
    options,
    maxAnswer,
    title,
    required,
    hasOtherOption,
    name,
    error
}: MatsuriFormsSelectQuestionProps) => {
    const { t } = useTranslation()
    const type = multiple ? "checkbox" : "radio"

    const [otherOptionSelected, setOtherOptionSelected] = useState(false)

    return (
        <div>
            <Typography variant="caption">
                {t(title)}
                {required ? (
                    <span
                        css={css`
                            color: red;
                        `}
                    >
                        *
                    </span>
                ) : null}
            </Typography>
            <div
                css={css`
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0px 24px;
                `}
            >
                {options.map((option, index) => {
                    return (
                        <Checkbox
                            key={index}
                            type={type}
                            name={name}
                            value={option.value}
                        >
                            {t(option.label)}
                        </Checkbox>
                    )
                })}
                {
                    // FIXME: 択一の時は自由入力には対応できない
                    // radioボタンとして制御するのはかなり難しそうなため
                    // cf: https://github.com/matsuri-tech/matsuri-forms-manager-front/issues/363
                    hasOtherOption && maxAnswer && maxAnswer > 1 ? (
                        <div
                            css={css`
                                display: flex;
                                gap: 8px;
                                align-items: center;
                            `}
                        >
                            <Checkbox
                                type={type}
                                name={name}
                                checked={otherOptionSelected}
                                onClick={() => {
                                    setOtherOptionSelected(prev => !prev)
                                }}
                            >
                                {t("自由入力")}
                            </Checkbox>
                            {otherOptionSelected ? (
                                <TextField name={name} />
                            ) : null}
                        </div>
                    ) : null
                }
            </div>
            {error ? (
                <Typography variant="caption" color="error">
                    {error.code === "too_many"
                        ? t(`選択できる選択肢は最大${maxAnswer}個です。`)
                        : error.message}
                </Typography>
            ) : null}
        </div>
    )
}

const MultipleTextQuestion = ({
    id,
    name,
    title,
    required,
    maxLength,
    description
}: MatsuriFormsMultilineTextQuestionProps) => {
    const { t } = useTranslation()
    const { registeredQuestions } = useTroubleForms()

    const onDescriptionChange = useContext(DescriptionContext)?.setDescription

    return (
        // NOTE: 質問がdescriptionに相当するものの場合、context経由でdescriptionを更新する
        id === registeredQuestions.descriptionId ? (
            <MultipleTextField
                name={name}
                label={t(title)}
                required={required}
                maxLength={maxLength}
                helperText={description ? t(description) : undefined}
                onChange={onDescriptionChange}
            />
        ) : (
            <MultipleTextField
                name={name}
                label={t(title)}
                required={required}
                maxLength={maxLength}
                helperText={description ? t(description) : undefined}
            />
        )
    )
}

const RenderText = (props: MatsuriFormsTextQuestionProps) => {
    const { t } = useTranslation()
    const { registeredQuestions, listingId, commonAreaId } = useTroubleForms()
    const inputProps = {
        name: props.name,
        label: t(props.title),
        maxLength: props.maxLength,
        helperText: JSON.stringify(props.error),
        required: props.required,
        description: props.description ? t(props.description) : undefined
    }
    const id = props.id

    return id === registeredQuestions.listingId ? (
        <input type="hidden" {...inputProps} value={listingId} />
    ) : id === registeredQuestions.commonAreaId ? (
        <input type="hidden" {...inputProps} value={commonAreaId} />
    ) : id === registeredQuestions.userId ? (
        <RenderUserId {...inputProps} />
    ) : id === registeredQuestions.userName ? (
        <RenderUserName {...inputProps} />
    ) : id === registeredQuestions.troubleId ? (
        // formの送信時にtroubleIdを送信するためinput要素はレンダリング不要
        <React.Fragment />
    ) : (
        <TextField
            {...inputProps}
            label={
                inputProps.description
                    ? `${inputProps.label} (${inputProps.description})`
                    : inputProps.label
            }
        />
    )
}

const RenderUserId = (props: React.ComponentPropsWithoutRef<"input">) => {
    const { token } = useAuth()
    const user = useAuthUser(token)

    return <input {...props} type="hidden" value={user.userId} />
}

const RenderUserName = (props: React.ComponentPropsWithoutRef<"input">) => {
    const { token } = useAuth()
    const user = useAuthUser(token)
    const { data: userDetail } = useFindUserById(token, user.userId)

    return <input {...props} type="hidden" value={userDetail?.name} />
}
